import { Alert, Button, ConfigProvider, Modal, Spin, message } from "antd";
import { Dispatch, SetStateAction, useEffect, useState, useCallback, useRef } from "react";
import container from "@/lib/di/container";
import { ElectronicContractRepo } from "@/core/repos/tables/electronic-contract.repo";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { PdfDocumentViewer } from './PdfDocumentViewer';

const electronicContractRepo = container.get(ElectronicContractRepo);

interface ViewDocumentModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    contractId: string,
    isV2?: boolean,
    onLoaded?: () => void,
}

export const ViewDocumentModal: React.FC<ViewDocumentModalProps> = ({isModalOpen, setIsModalOpen, contractId, isV2 = false, onLoaded}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [documentUrl, setDocumentUrl] = useState<string>("");
    const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
    const [error, setError] = useState(false);
    const objectUrl = useRef<string | null>(null);
    const [messageApi, contextHolder] = message.useMessage();
    const generation = useRef(0);
    const loadedCallback = useRef(onLoaded);
    loadedCallback.current = onLoaded;

    const loadDocument = useCallback(async () => {
        const version = ++generation.current;
        setLoading(true);
        setDocumentUrl("");
        setPdfData(null); setError(false);
        if (objectUrl.current) { URL.revokeObjectURL(objectUrl.current); objectUrl.current = null; }
        try {
            let url: string | null = null;
            if (isV2) {
                const response = await electronicContractRepo.getPdfContent(contractId);
                if (version !== generation.current) return;
                if (response?.code !== '00' || !response.result?.pdfBase64) throw new Error('PDF unavailable');
                const encoded = response.result.pdfBase64;
                if (encoded.length > 28 * 1024 * 1024) throw new Error('PDF too large');
                const raw = atob(encoded);
                if (!raw.startsWith('%PDF-') || raw.length > 20 * 1024 * 1024) throw new Error('Invalid PDF');
                const data = Uint8Array.from(raw, char => char.charCodeAt(0));
                const localUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
                objectUrl.current = localUrl;
                setDocumentUrl(localUrl); setPdfData(data);
                return;
            } else {
                url = await electronicContractRepo.viewDocument({ electronicContractId: contractId, type:2 });
            }
            if (version !== generation.current) return;
            if (url) {
                // Check if it's a base64 data URL or regular URL
                if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) {
                    setDocumentUrl(url);
                } else {
                    // Assume it's base64 data and create a data URL
                    setDocumentUrl(`data:application/pdf;base64,${url}`);
                }
                loadedCallback.current?.();
            } else {
                messageApi.error({
                    content: "Không thể tải tài liệu"
                });
            }
        } catch (error) {
            if (version === generation.current) setError(true);
        } finally {
            if (version === generation.current) setLoading(false);
        }
    }, [contractId, isV2, messageApi]);

    useEffect(() => {
        if (isModalOpen && contractId) {
            loadDocument();
        } else {
            generation.current += 1;
            setDocumentUrl("");
            setPdfData(null);
        }
        return () => {
            generation.current += 1;
            if (objectUrl.current) { URL.revokeObjectURL(objectUrl.current); objectUrl.current = null; }
        };
    }, [isModalOpen, contractId, loadDocument]);

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    // Unmount the canvas/worker immediately; Modal otherwise retains its hidden children.
    if (!isModalOpen) return null;

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal 
                open={isModalOpen} 
                onCancel={handleCancel} 
                width={1200} 
                centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={null}
            >
                <div className="flex flex-row w-full items-center justify-start select-none mb-4">
                    <p className="ml-2 text-2xl font-bold text-primary">XEM TÀI LIỆU HỢP ĐỒNG</p>
                </div>
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <Spin size="large" />
                    </div>
                ) : error ? (
                    <><Alert type="error" showIcon message="Không thể hiển thị PDF" description="Không tải được tài liệu hợp lệ. Vui lòng thử lại." /><Button className="mt-3" onClick={loadDocument}>Tải lại PDF</Button></>
                ) : documentUrl ? (
                    <><div className="mb-3 flex gap-2">
                        {pdfData ? <Button href={documentUrl} download={`hop-dong-${contractId}.pdf`}>Tải PDF</Button> : <Button href={documentUrl} target="_blank" rel="noopener noreferrer">Mở PDF</Button>}
                        <Button onClick={loadDocument}>Lấy lại PDF</Button>
                    </div>{pdfData ? <PdfDocumentViewer data={pdfData} onRendered={() => loadedCallback.current?.()} /> : <div className="w-full" style={{ height: '70vh' }}>
                        <iframe
                            src={documentUrl}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title="Document Viewer"
                        />
                    </div>}</>
                ) : (
                    <div className="text-center py-12">
                        <p>Không có tài liệu để hiển thị</p>
                        <Button className="mt-3" onClick={loadDocument}>Tải lại PDF</Button>
                    </div>
                )}
            </Modal>
        </ConfigProvider>
    );
};

